import { useLingui } from '@lingui/react/macro'
import { usePdfAsImage } from 'features/knowledgeDocumentViewer/components/viewer/image/hooks/usePdfAsImage'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { usePageImageFailure } from 'features/knowledgeDocumentViewer/hooks/usePageImageFailure'
import { usePageImageOutcome } from 'features/knowledgeDocumentViewer/hooks/usePageImageOutcome'
import {
  imageEventSrc,
  usePageImageRecovery,
} from 'features/knowledgeDocumentViewer/hooks/usePageImageRecovery'
import { useViewerCantileverPageImageRef } from 'features/knowledgeDocumentViewer/hooks/useViewerCantileverPageImageRef'
import {
  resolvePageImageRolodexSettleState,
  resolvePdfUrlForPageImageFallback,
} from 'features/knowledgeDocumentViewer/utils/pageImageRolodexSettle'
import { useRolodexSignedURLs } from 'features/rolodex'
import React, { useCallback, useEffect } from 'react'

import { PageImage } from './continuousScrollStyles'

const CONTINUOUS_PDF_SCALE = 4

export type PageImageDisplayFailure = { retry: () => void }

interface ContinuousPageImageProps {
  pageNumber: number
  pdfUrl: string | undefined
  totalPages: number
  isCurrentPage: boolean
  onDimensionsKnown?: (
    pageNumber: number,
    width: number,
    height: number
  ) => void
  onRolodexSourceHidden?: (pageNumber: number, hidden: boolean) => void
  onDisplayStateChange?: (
    pageNumber: number,
    failure: PageImageDisplayFailure | null
  ) => void
}

export const ContinuousPageImage = React.memo(function ContinuousPageImage({
  pageNumber,
  pdfUrl,
  totalPages,
  isCurrentPage,
  onDimensionsKnown,
  onRolodexSourceHidden,
  onDisplayStateChange,
}: ContinuousPageImageProps) {
  const { t } = useLingui()
  const { isUuid5PageImageLookupUnavailable, imageDocumentId } =
    useDocumentTabDataContext()
  const hiResQuery = useViewerCantileverPageImageRef(pageNumber, 'hiRes')
  const {
    url: hiResUrl,
    isLoading: isHiResLoading,
    forceRefresh,
  } = useRolodexSignedURLs(hiResQuery)

  const imageKey = `${imageDocumentId ?? ''}:${pageNumber}`
  const outcome = usePageImageOutcome({
    pageNumber,
    surface: 'continuous',
    isCurrentPage,
  })
  const recovery = usePageImageRecovery({
    resetKey: imageKey,
    url: hiResUrl,
    forceRefresh,
    onRetry: outcome.markRetried,
  })

  const rolodexHidden = recovery.exhausted || recovery.retrying
  useEffect(() => {
    if (!rolodexHidden) return
    onRolodexSourceHidden?.(pageNumber, true)
    return () => onRolodexSourceHidden?.(pageNumber, false)
  }, [onRolodexSourceHidden, pageNumber, rolodexHidden])

  const hiResSettle = resolvePageImageRolodexSettleState({
    hasQuery: hiResQuery !== undefined,
    lookupUnavailable: isUuid5PageImageLookupUnavailable,
    url: recovery.url,
    isLoading: isHiResLoading,
  })
  const pdfUrlForFallback = resolvePdfUrlForPageImageFallback(
    hiResSettle,
    pdfUrl
  )
  const pdfFallbackEnabled = pdfUrlForFallback !== undefined
  const pdf = usePdfAsImage(
    pdfUrlForFallback,
    pageNumber,
    CONTINUOUS_PDF_SCALE,
    totalPages,
    pdfFallbackEnabled
  )

  const displayUrl = recovery.url ?? (pdf.stale ? null : pdf.imageUrl)

  const failure = usePageImageFailure({
    imageKey,
    rolodexUrl: hiResUrl,
    recovery,
    outcome,
    pdf,
    pdfAttempted: pdfFallbackEnabled,
    hasDisplayUrl: !!displayUrl,
    isLoading: isHiResLoading || pdf.isLoading,
    rolodexMissed: hiResSettle.isFailed,
    lookupUnavailable: isUuid5PageImageLookupUnavailable,
    isCurrentPage,
  })

  const { failed, retry } = failure
  useEffect(() => {
    if (!failed) return
    onDisplayStateChange?.(pageNumber, { retry })
    return () => onDisplayStateChange?.(pageNumber, null)
  }, [failed, onDisplayStateChange, pageNumber, retry])

  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const src = imageEventSrc(e)
      recovery.onLoad(src)
      outcome.reportDisplayed(src === hiResUrl ? 'uuid5' : 'pdf')
      onDimensionsKnown?.(
        pageNumber,
        e.currentTarget.naturalWidth,
        e.currentTarget.naturalHeight
      )
    },
    [hiResUrl, onDimensionsKnown, outcome, pageNumber, recovery]
  )

  const handleError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) =>
      failure.onImageError(imageEventSrc(e)),
    [failure]
  )

  if (!displayUrl) return null

  return (
    <PageImage
      src={displayUrl}
      alt={t`Page ${pageNumber}`}
      onLoad={handleLoad}
      onError={handleError}
      draggable={false}
    />
  )
})
